<template>
  <Transition name="dialog">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 z-50 overflow-y-auto"
      @click.self="handleCancel"
    >
      <!-- 改為 items-start 並加上適當的 padding -->
      <div class="flex min-h-full items-start justify-center px-6 py-12">
        <div 
          ref="dialogRef"
          class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          @click.stop
        >
          <!-- 標題 -->
          <h3 class="text-lg font-bold text-textMain mb-4">建立帳號</h3>

          <!-- 說明 -->
          <p class="text-xs text-textMuted mb-4">
            建立帳號後，您的資料將與此信箱綁定，可在其他裝置登入。此信箱將用於找回資料。若填錯信箱，未來可能無法恢復。
          </p>

          <!-- 表單 -->
          <div class="space-y-4">
            <!-- Email -->
            <div>
              <label class="block text-sm font-bold text-textMain mb-2">信箱</label>
              <input
                ref="emailInput"
                v-model="email"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="your@email.com"
                class="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                :class="{ 'border-red-400': emailError }"
                @focus="scrollIntoViewOnFocus"
              />
              <p v-if="emailError" class="text-xs text-red-500 mt-1">{{ emailError }}</p>
            </div>

            <!-- Password -->
            <div>
              <label class="block text-sm font-bold text-textMain mb-2">密碼</label>
              <div class="relative">
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  inputmode="text"
                  autocomplete="new-password"
                  placeholder="至少 8 碼"
                  class="w-full px-4 py-3 pr-12 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                  :class="{ 'border-red-400': passwordError }"
                  @focus="scrollIntoViewOnFocus"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <!-- 睁开的眼睛 -->
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <!-- 闭上的眼睛 -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <p v-if="passwordError" class="text-xs text-red-500 mt-1">{{ passwordError }}</p>
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="block text-sm font-bold text-textMain mb-2">確認密碼</label>
              <div class="relative">
                <input
                  ref="confirmPasswordInput"
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  inputmode="text"
                  autocomplete="new-password"
                  placeholder="再次輸入密碼"
                  class="w-full px-4 py-3 pr-12 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                  :class="{ 'border-red-400': confirmPasswordError }"
                  @focus="scrollIntoViewOnFocus"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <!-- 睁开的眼睛 -->
                  <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <!-- 闭上的眼睛 -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <p v-if="confirmPasswordError" class="text-xs text-red-500 mt-1">{{ confirmPasswordError }}</p>
            </div>
          </div>

          <!-- 錯誤訊息 -->
          <div v-if="generalError" class="mt-4 p-3 bg-red-50 rounded-xl">
            <p class="text-xs text-red-600">{{ generalError }}</p>
          </div>

          <!-- 按鈕 -->
          <div class="flex gap-3 mt-6">
            <button
              @click="handleCancel"
              class="flex-1 py-3 rounded-xl font-bold text-textMuted bg-gray-100 active:scale-95 transition-all"
              :disabled="loading"
            >
              取消
            </button>
            <button
              @click="handleSubmit"
              class="flex-1 py-3 rounded-xl font-bold text-white bg-primary active:scale-95 transition-all disabled:opacity-50"
              :disabled="loading || !isValid"
            >
              {{ loading ? '建立中...' : '建立帳號' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'

interface Props {
  show: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  confirm: [email: string, password: string]
  cancel: []
}>()

// Template refs for iOS keyboard/focus improvements
const dialogRef = ref<HTMLElement>()
const emailInput = ref<HTMLInputElement>()
const confirmPasswordInput = ref<HTMLInputElement>()

const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const emailError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')
const generalError = ref('')

// 當 dialog 打開時，自動 focus 到 email 輸入框
watch(() => props.show, (newShow) => {
  if (newShow) {
    nextTick(() => {
      // 延遲一點讓動畫完成
      setTimeout(() => {
        emailInput.value?.focus()
        dialogRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 200)
    })
  }
})

// 當輸入框獲得焦點時，滾動到可見位置
const scrollIntoViewOnFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement
  
  // 延遲執行，等待鍵盤彈出
  setTimeout(() => {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }, 300)
}

// Email 格式驗證
const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

// 驗證表單
const isValid = computed(() => {
  return email.value && password.value && confirmPassword.value &&
         !emailError.value && !passwordError.value && !confirmPasswordError.value
})

// 即時驗證 - 當輸入改變時自動驗證
watch(email, () => {
  if (emailError.value) {  // 只在有錯誤時才驗證
    if (!email.value) {
      emailError.value = '請輸入信箱'
    } else if (!validateEmail(email.value)) {
      emailError.value = '信箱格式不正確'
    } else {
      emailError.value = ''
    }
  }
  generalError.value = ''
})

watch(password, () => {
  if (passwordError.value) {  // 只在有錯誤時才驗證
    if (!password.value) {
      passwordError.value = '請輸入密碼'
    } else if (password.value.length < 8) {
      passwordError.value = '密碼至少需要 8 碼'
    } else {
      passwordError.value = ''
    }
  }
  // 密碼改變時，也要重新驗證確認密碼
  if (confirmPassword.value && confirmPasswordError.value) {
    if (password.value !== confirmPassword.value) {
      confirmPasswordError.value = '密碼不一致'
    } else {
      confirmPasswordError.value = ''
    }
  }
  generalError.value = ''
})

watch(confirmPassword, () => {
  if (confirmPasswordError.value) {  // 只在有錯誤時才驗證
    if (!confirmPassword.value) {
      confirmPasswordError.value = '請再次輸入密碼'
    } else if (password.value !== confirmPassword.value) {
      confirmPasswordError.value = '密碼不一致'
    } else {
      confirmPasswordError.value = ''
    }
  }
  generalError.value = ''
})

// 即時驗證
const validateForm = () => {
  // 驗證 Email
  if (!email.value) {
    emailError.value = '請輸入信箱'
  } else if (!validateEmail(email.value)) {
    emailError.value = '信箱格式不正確'
  } else {
    emailError.value = ''
  }

  // 驗證密碼
  if (!password.value) {
    passwordError.value = '請輸入密碼'
  } else if (password.value.length < 8) {
    passwordError.value = '密碼至少需要 8 碼'
  } else {
    passwordError.value = ''
  }

  // 驗證確認密碼
  if (!confirmPassword.value) {
    confirmPasswordError.value = '請再次輸入密碼'
  } else if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = '密碼不一致'
    // 密碼不一致時，focus 到確認密碼欄位
    nextTick(() => confirmPasswordInput.value?.focus())
  } else {
    confirmPasswordError.value = ''
  }

  generalError.value = ''
}

const handleSubmit = () => {
  validateForm()

  if (!isValid.value) return

  loading.value = true
  emit('confirm', email.value, password.value)
}

const handleCancel = () => {
  if (loading.value) return
  resetForm()
  emit('cancel')
}

const resetForm = () => {
  email.value = ''
  password.value = ''
  confirmPassword.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
  emailError.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''
  generalError.value = ''
  loading.value = false
}

const setError = (error: string) => {
  generalError.value = error
  loading.value = false
}

const setLoading = (value: boolean) => {
  loading.value = value
}

defineExpose({
  setError,
  resetForm,
  setLoading
})
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}

.animate-in {
  animation: fade-in 0.2s ease, zoom-in-95 0.2s ease;
}
</style>